import type { JSX } from 'react';
import { WhatsAppIcon } from '@components/icons/WhatsAppIcon';
import { Button } from '@components/ui/Button';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { landingCopy } from '@features/landing/data/landing.pt';
import { trackWhatsAppClick } from '@lib/analytics';
import { whatsappLink } from '@lib/whatsappLink';

const FINAL_SOURCE_ID = 'final-cta';

/**
 * Secao "CTA Final" (SECT-08 — PRD §4.9).
 *
 * Faixa verde escura `bg-brand-700` full-width que fecha a landing com a
 * conversao final antes do rodape. Conteudo:
 *  - Titulo grande centralizado: "Vamos conversar sobre o que sua empresa
 *    realmente precisa?" (font-display, white, text-3xl-to-5xl)
 *  - Subtexto neutro: "Sem compromisso, sem proposta forçada..."
 *    (text-lg, brand-100/90 — contraste WCAG AA AAA)
 *  - Botao grande: "Falar no WhatsApp" — Button primary size lg INVERTIDO
 *    (bg-white text-brand-700 hover:bg-brand-50) — destaca do fundo verde
 *
 * sourceId='final-cta' distingue cliques no analytics (CONV-03).
 *
 * Decisao de variante: Como o Button primary atual usa `bg-brand-500
 * text-white`, sobre o `bg-brand-700` da banda o botao ficaria nao-destacavel
 * (verde-em-verde com contraste fraco). Usamos `className` para override
 * pontual em vez de adicionar uma 4a variant `inverse` ao Button — a Phase 5
 * e o unico consumer de pill-on-dark no MVP. Se Phase 6 introduzir outros
 * dark surfaces consumindo o Button, reavaliar e mover override para variant.
 *
 * Acessibilidade:
 *  - `<section aria-labelledby="cta-final-heading">`
 *  - Decorative blob brand-600 atras do titulo (aria-hidden, sem custo de
 *    asset)
 *  - Contraste: white em brand-700 = 6.4:1 (AAA Large)
 */
export const CtaFinal = (): JSX.Element => {
  const { ctaFinal } = landingCopy;
  const whatsappHref = whatsappLink(landingCopy.whatsapp.defaultMessage, FINAL_SOURCE_ID);

  return (
    <section
      id="cta-final"
      aria-labelledby="cta-final-heading"
      className="relative overflow-hidden bg-brand-700 py-20 text-white lg:py-28"
    >
      {/* Decorative organic blob atras do titulo, brand-600 mais claro */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-600/30 blur-3xl lg:h-96 lg:w-96"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -right-20 h-72 w-72 rounded-full bg-brand-800/40 blur-3xl lg:h-96 lg:w-96"
      />

      <Container className="relative z-10">
        <RevealOnView className="mx-auto max-w-3xl text-center">
          <h2
            id="cta-final-heading"
            className="font-display text-3xl font-bold leading-tight md:text-4xl lg:text-5xl"
          >
            {ctaFinal.title}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-brand-100/90 sm:text-xl">
            {ctaFinal.subtitle}
          </p>
          <div className="mt-10 flex justify-center">
            <Button
              variant="primary"
              size="lg"
              as="a"
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={ctaFinal.buttonAriaLabel}
              icon={<WhatsAppIcon className="h-5 w-5" />}
              onClick={() => {
                trackWhatsAppClick(FINAL_SOURCE_ID);
              }}
              className="!bg-white !text-brand-700 hover:!bg-brand-50 hover:!text-brand-800 !shadow-xl focus-visible:!ring-white focus-visible:!ring-offset-brand-700"
            >
              {ctaFinal.buttonLabel}
            </Button>
          </div>
        </RevealOnView>
      </Container>
    </section>
  );
};
